import React, { useCallback, useEffect, useState } from "react";
import { ParsedIngredientSummary, StandardPrimaryCategory } from "@eatbetter/items-shared";
import {
  ButtonRectangle,
  colorPalette,
  CurrentEnvironment,
  displayUnexpectedErrorAndLog,
  getPhotoUrl,
  globalStyleConstants,
  Photo,
  Pressable,
  SetWaitingHandler,
  Spacer,
  TBody,
  THeading1,
  THeading2,
} from "@eatbetter/ui-shared";
import { Link, useNavigate } from "react-router-dom";
import { AdminRecipeEditScreenNav } from "../screens/AdminRecipeEditScreen";
import { View } from "react-native";
import {
  AuthorComponent,
  BookComponent,
  CategorizedIngredients,
  IndexComponent,
  Ingredients,
  Instructions,
  PhotoComponent,
  PublisherComponent,
  TagsComponent,
  TimeComponent,
  YieldComponent,
} from "./AdminRecipeComponents";
import { ExternalRecipe, MediaItem, RecipeGroupInfo, RecipeId, RecipeTagManifest } from "@eatbetter/recipes-shared";
import { daysBetween, defaultTimeProvider, switchReturn, UrlString } from "@eatbetter/common-shared";
import { useDispatch } from "../lib/AdminRedux";
import {
  getRecipeOrThrow,
  saveRecipeCopy,
  setAdminRecipeText,
  setGroupMemberExcluded,
} from "../lib/recipes/AdminRecipesThunks";
import { UserSearchSelect } from "./SearchSelect";
import { NvpTable } from "./NvpTable";
import { AdminGetRecipeResult, RecipeCopies } from "@eatbetter/composite-shared";
import { AnonymousUser, DeglazeUser } from "@eatbetter/users-shared";
import { Checkbox, Collapse, Divider, Modal } from "antd";
import { AdminRecipeOrphanScreenNav } from "../screens/AdminRecipeOrphanScreen";
import { RecipeLink } from "./RecipeLink";
import { addIngredientCategoryOverride } from "../lib/lists/AdminListsThunks";
import { AdminCategoryOverrideArgs, IngredientCategoryOverride } from "@eatbetter/lists-shared";
import { AdminRecipeDocumentsScreenNav } from "../screens/AdminRecipeDocumentsScreen";
import { ReprocessRecipeModal } from "./ReprocessRecipeModal";

export const ExternalRecipeView = React.memo(
  (props: {
    recipe: ExternalRecipe;
    parsed: ParsedIngredientSummary[];
    overrides?: IngredientCategoryOverride[];
    setRecipe: (updated: AdminGetRecipeResult) => void;
    tagManifest: RecipeTagManifest;
    recipeCopies?: RecipeCopies;
    groupInfo?: RecipeGroupInfo;
  }) => {
    const dispatch = useDispatch();

    const recipe = props.recipe;

    // we set this when the prop value changes *or* when we make an update call, hence the state variable
    const [overrides, setOverrides] = useState<IngredientCategoryOverride[] | undefined>(props.overrides);

    useEffect(() => {
      setOverrides(props.overrides);
    }, [props.overrides]);

    const createdDaysAgo = daysBetween(recipe.created, defaultTimeProvider()).toFixed(1);
    const versionDaysAgo = daysBetween(recipe.version, defaultTimeProvider()).toFixed(1);
    const s3Key = recipe.id.replaceAll(":", "!");
    const s3Url =
      `https://s3.console.aws.amazon.com/s3/buckets/deglaze-${CurrentEnvironment.configEnvironment()}-recipes?prefix=${s3Key}/&region=us-west-2` as UrlString;
    const nav = useNavigate();

    const onEdit = () => {
      if (!recipe) {
        return;
      }

      nav(AdminRecipeEditScreenNav.getPath(recipe.id));
    };

    const [showReprocessDialog, setShowReprocessDialog] = useState(false);
    const [showGroupDialog, setShowGroupDialog] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<(DeglazeUser | Omit<AnonymousUser, "plan">)[]>([]);

    // Local copy of the group info so an exclude/include toggle reflects immediately in the modal.
    const [groupInfo, setGroupInfo] = useState(props.groupInfo);
    const [excludeWaitingId, setExcludeWaitingId] = useState<string | undefined>(undefined);
    useEffect(() => {
      setGroupInfo(props.groupInfo);
    }, [props.groupInfo]);

    const onToggleExcluded = useCallback(
      async (memberRecipeId: string, excluded: boolean) => {
        if (!groupInfo) {
          return;
        }
        setExcludeWaitingId(memberRecipeId);
        try {
          const updated = await dispatch(
            setGroupMemberExcluded({ groupId: groupInfo.groupId, recipeId: memberRecipeId as RecipeId, excluded })
          );
          if (updated) {
            setGroupInfo(updated);
          }
        } catch (err) {
          displayUnexpectedErrorAndLog("Error dispatching setGroupMemberExcluded", err, { memberRecipeId });
        } finally {
          setExcludeWaitingId(undefined);
        }
      },
      [dispatch, groupInfo]
    );

    const currentAdminRecipeText = recipe.type === "externalUrlRecipe" ? recipe.adminRecipeText ?? "" : "";
    const [isEditingAdminText, setIsEditingAdminText] = useState(false);
    const [adminTextDraft, setAdminTextDraft] = useState(currentAdminRecipeText);
    const [adminTextWaiting, setAdminTextWaiting] = useState(false);
    const [adminTextExpanded, setAdminTextExpanded] = useState(false);
    const adminTextIsLong = currentAdminRecipeText.split("\n").length > 3 || currentAdminRecipeText.length > 200;

    useEffect(() => {
      setAdminTextDraft(currentAdminRecipeText);
      setIsEditingAdminText(false);
    }, [currentAdminRecipeText]);

    const saveAdminRecipeText = useCallback(
      async (text: string | undefined) => {
        if (recipe.type !== "externalUrlRecipe") {
          return;
        }
        setAdminTextWaiting(true);
        try {
          await dispatch(setAdminRecipeText({ recipeId: recipe.id, adminRecipeText: text }));
          const refreshed = await dispatch(getRecipeOrThrow(recipe.id));
          props.setRecipe(refreshed);
          setIsEditingAdminText(false);
        } catch (err) {
          displayUnexpectedErrorAndLog("Error dispatching setAdminRecipeText", err, { recipeId: recipe.id });
        } finally {
          setAdminTextWaiting(false);
        }
      },
      [dispatch, recipe, props]
    );

    const sendToUsers = useCallback(async () => {
      await Promise.all(
        selectedUsers.map(i => {
          return dispatch(saveRecipeCopy({ userId: i.userId, sourceRecipeId: recipe.id }));
        })
      );

      alert(`Success! Recipe pushed to ${selectedUsers.length} user(s).`);
    }, [selectedUsers, dispatch]);

    const onCategoryOverride = (
      text: string,
      category: StandardPrimaryCategory | null,
      setWaiting: SetWaitingHandler
    ) => {
      const args: AdminCategoryOverrideArgs = { recipeId: props.recipe.id, category, ingredientText: text };
      dispatch(addIngredientCategoryOverride(args, setWaiting))
        .then(o => {
          setOverrides(o.adminCategoryOverrides);
        })
        .catch(err => {
          displayUnexpectedErrorAndLog("Error dispatching addIngredientCategoryOverride", err, { args });
        });
    };

    const screenTitle = switchReturn(props.recipe.type, {
      externalBookRecipe: "External Book Recipe",
      externalUrlRecipe: "External URL Recipe",
    });

    const otherUrls = recipe.type === "externalUrlRecipe" && recipe.otherUrls ? recipe.otherUrls : [];

    const primaryRecipeId = recipe.type === "externalUrlRecipe" ? recipe.multiSource?.primary : undefined;
    const currentIsPrimary = primaryRecipeId === recipe.id;

    // A "collapsed" group: every non-primary member excluded, so only the primary is active. The primary
    // then behaves as a standalone recipe (no relations, reprocessable).
    const siblings = groupInfo?.members.filter(m => !m.isPrimary) ?? [];
    const allSiblingsExcluded = siblings.length > 0 && siblings.every(m => !!m.excluded);

    return (
      <View>
        <THeading1 fontWeight="medium">{`${screenTitle} ${recipe.id}`}</THeading1>
        <Spacer vertical={1} />
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <View style={{ width: globalStyleConstants.unitSize * 12 }}>
            <ButtonRectangle onPress={onEdit} type="submit" title="Edit" />
          </View>
          <Spacer horizontal={8} />
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <View style={{ width: 24 * globalStyleConstants.unitSize }}>
              <UserSearchSelect mode="multi" selected={selectedUsers} onChange={setSelectedUsers} />
            </View>
            <Spacer horizontal={1} />
            <ButtonRectangle type="submit" title="Send" onPress={sendToUsers} />
          </View>
          <Spacer horizontal={1} />
          <View>
            <ButtonRectangle onPress={() => setShowReprocessDialog(true)} type="submit" title="Reprocess" />
          </View>
          {props.recipe.type === "externalUrlRecipe" && (
            <>
              <Spacer horizontal={1} />
              <View>
                <ButtonRectangle
                  type="cancel"
                  title="Orphan"
                  // Orphaning a carousel group member is unsupported (breaks the group; no reconciliation).
                  disabled={!!props.recipe.multiSource}
                  onPress={() => nav(AdminRecipeOrphanScreenNav.getPath(recipe.id))}
                />
              </View>
            </>
          )}
        </View>
        <Spacer vertical={1} />
        <table>
          <tbody>
            <tr>
              <td>
                <TBody fontWeight="heavy">Meta</TBody>
              </td>
            </tr>
            <NvpTable name="ID" value={recipe.id} />
            {recipe.type === "externalUrlRecipe" && !!recipe.orphanedBy && (
              <NvpTable name="Orphaned by" backgroundColor="red">
                <RecipeLink id={recipe.orphanedBy} />
              </NvpTable>
            )}
            {recipe.type === "externalUrlRecipe" && <NvpTable name="Status" value={recipe.status} />}
            {recipe.type === "externalUrlRecipe" && (
              <NvpTable name="Processing Meta" value={JSON.stringify(recipe.processingMeta)} />
            )}
            {!!groupInfo && (
              <NvpTable name="Group">
                <Pressable onPress={() => setShowGroupDialog(true)}>
                  <TBody color={colorPalette.skyBlue}>
                    {`${allSiblingsExcluded ? "ALL EXCLUDED — " : ""}${
                      currentIsPrimary ? "Primary" : "Non-primary"
                    } of ${groupInfo.groupId} (${groupInfo.members.length} recipes)`}
                  </TBody>
                </Pressable>
              </NvpTable>
            )}
            <NvpTable name="Created" value={`${recipe.created} (${createdDaysAgo} days ago)`} />
            <NvpTable name="Version" value={`${recipe.version} (${versionDaysAgo} days ago)`} />
            <NvpTable name="S3">
              <TBody>
                <a href={s3Url} target="_blank" rel="noreferrer">
                  {s3Url}
                </a>
              </TBody>
            </NvpTable>
            {recipe.type === "externalUrlRecipe" && (
              <>
                <NvpTable name="Last updated by" value={recipe.manuallyUpdatedBy ?? "<none>"} />
                <NvpTable
                  name="Admin Updated fields"
                  value={recipe.manuallyUpdatedFields.length === 0 ? "<none>" : recipe.manuallyUpdatedFields.join(", ")}
                />
                <NvpTable
                  name="AI Updated fields"
                  value={recipe.aiUpdatedFields.length === 0 ? "<none>" : recipe.aiUpdatedFields.join(", ")}
                  backgroundColor={recipe.aiUpdatedFields.length > 0 ? "yellow" : undefined}
                />
                <NvpTable name="Ingestion Source" value={recipe.ingestionSource ?? "-"} />
                <NvpTable name="Full Access" value={JSON.stringify(recipe.faInfo)} />
                <NvpTable name="Search Indexing">
                  <IndexComponent recipe={recipe} />
                </NvpTable>
                <NvpTable
                  name="Date Published"
                  value={recipe.datePublished ? new Date(recipe.datePublished).toLocaleString() : "-"}
                />
                <NvpTable
                  name="Date Modified"
                  value={recipe.dateModified ? new Date(recipe.dateModified).toLocaleString() : "-"}
                />
                <NvpTable name="">
                  <TBody>
                    <Link to={AdminRecipeDocumentsScreenNav.getPath(recipe.id)} target="_blank">
                      Docs
                    </Link>
                  </TBody>
                </NvpTable>
              </>
            )}

            {recipe.type === "externalBookRecipe" && (
              <>
                <NvpTable name="Full Access" value={recipe.faInfo ? JSON.stringify(recipe.faInfo) : "-"} />
                <NvpTable name="Search Indexing">
                  <IndexComponent recipe={recipe} />
                </NvpTable>
                <Spacer vertical={2} />
                <BookComponent book={recipe.book} />
              </>
            )}
            <Spacer vertical={2} />
            <tr>
              <td>
                <TBody fontWeight="heavy">Recipe</TBody>
              </td>
            </tr>
            {recipe.type === "externalUrlRecipe" && (
              <>
                <NvpTable name="URL">
                  <TBody>
                    <a href={recipe.canonicalUrl} target="_blank" rel="noreferrer">
                      {recipe.canonicalUrl}
                    </a>
                  </TBody>
                </NvpTable>
                <NvpTable name="Not Canonical" value={`${recipe.noCanonicalUrl}`} />
              </>
            )}
            {otherUrls.map(u => {
              return (
                <NvpTable name="Secondary URL" opacity={0.5} key={u}>
                  <TBody>
                    <a href={u} target="_blank" rel="noreferrer">
                      {u}
                    </a>
                  </TBody>
                </NvpTable>
              );
            })}
            <NvpTable name="Title">
              <THeading2>{recipe.title}</THeading2>
            </NvpTable>
            <Divider />
            <NvpTable name="Photo">
              <PhotoComponent photo={recipe.photo} size="large" />
            </NvpTable>
            <Divider />
            <NvpTable name="Author">
              <AuthorComponent
                author={recipe.author}
                parsedAuthor={recipe.type === "externalUrlRecipe" ? recipe.parsedAuthor : undefined}
              />
            </NvpTable>
            <Divider />
            <NvpTable name="Publisher">
              <PublisherComponent publisher={recipe.publisher} />
            </NvpTable>
            <Divider />
            <NvpTable name="Description">
              <TBody>{recipe.description}</TBody>
            </NvpTable>
            {recipe.type === "externalUrlRecipe" && !!recipe.socialPostInfo && (
              <NvpTable name="Social Caption">
                <TBody>
                  {recipe.socialPostInfo.caption !== recipe.description
                    ? recipe.socialPostInfo.caption
                    : "<same as description>"}
                </TBody>
              </NvpTable>
            )}
            {recipe.type === "externalUrlRecipe" && !!recipe.socialPostInfo?.media?.length && (
              <NvpTable name="Social Photos">
                <MediaThumbnailStrip media={recipe.socialPostInfo.media} />
              </NvpTable>
            )}
            {recipe.type === "externalUrlRecipe" && (
              <NvpTable name="Admin Recipe Text">
                {isEditingAdminText ? (
                  <View>
                    <textarea
                      value={adminTextDraft}
                      onChange={e => setAdminTextDraft(e.target.value)}
                      readOnly={adminTextWaiting}
                      rows={8}
                      style={{ width: "100%", fontFamily: "inherit", fontSize: "inherit" }}
                    />
                    <Spacer vertical={1} />
                    <View style={{ flexDirection: "row" }}>
                      <ButtonRectangle
                        type="submit"
                        title="Save"
                        onPress={() => saveAdminRecipeText(adminTextDraft.trim() || undefined)}
                        waiting={adminTextWaiting}
                        disabled={adminTextWaiting}
                      />
                      <Spacer horizontal={1} />
                      <ButtonRectangle
                        type="cancel"
                        title="Clear"
                        onPress={() => saveAdminRecipeText(undefined)}
                        waiting={adminTextWaiting}
                        disabled={adminTextWaiting}
                      />
                      <Spacer horizontal={1} />
                      <ButtonRectangle
                        type="cancel"
                        title="Cancel"
                        onPress={() => {
                          setAdminTextDraft(currentAdminRecipeText);
                          setIsEditingAdminText(false);
                        }}
                        disabled={adminTextWaiting}
                      />
                    </View>
                  </View>
                ) : (
                  <View>
                    <Pressable onPress={() => setIsEditingAdminText(true)}>
                      <TBody color={colorPalette.skyBlue}>edit</TBody>
                    </Pressable>
                    <TBody numberOfLines={adminTextExpanded || !adminTextIsLong ? undefined : 3}>
                      {recipe.adminRecipeText ?? "<none>"}
                    </TBody>
                    {adminTextIsLong && (
                      <Pressable onPress={() => setAdminTextExpanded(v => !v)}>
                        <TBody color={colorPalette.skyBlue}>{adminTextExpanded ? "show less" : "show more"}</TBody>
                      </Pressable>
                    )}
                  </View>
                )}
              </NvpTable>
            )}
            <Divider />
            <NvpTable name="Time">
              <TimeComponent
                time={recipe.time}
                timeRaw={recipe.type === "externalUrlRecipe" ? recipe.rawTime : undefined}
              />
            </NvpTable>
            <Divider />
            <NvpTable name="Yield">
              <YieldComponent
                yield={recipe.recipeYield}
                yieldRaw={recipe.type === "externalUrlRecipe" ? recipe.rawRecipeYield : undefined}
              />
            </NvpTable>
            <Divider />
            <NvpTable name="Tags">
              <TagsComponent manifest={props.tagManifest} tags={recipe.tags} />
            </NvpTable>
            <Divider />
            <NvpTable name="Ingredients">
              <Ingredients ingredients={recipe.ingredients} />
            </NvpTable>
            <Divider />
            <NvpTable name="Instructions">
              <Instructions instructions={recipe.instructions} />
            </NvpTable>
            <Divider />
            <NvpTable name="Categorized">
              <CategorizedIngredients items={props.parsed} overrides={overrides} onOverride={onCategoryOverride} />
            </NvpTable>
          </tbody>
        </table>
        {props.recipeCopies && (
          <THeading2>
            {props.recipeCopies.copies.length} {props.recipeCopies.copies.length === 1 ? "copy" : "copies"}{" "}
            {`${props.recipeCopies.mightBeMore ? "(or more)" : ""}`}
          </THeading2>
        )}
        {props.recipeCopies?.copies.map(c => {
          return (
            <View style={{ flexDirection: "row" }} key={c.recipeId}>
              <RecipeLink id={c.recipeId} />
              <Spacer horizontal={1} />
              {(!!c.deleted || !!c.archived) && <TBody> (archived or deleted)</TBody>}
            </View>
          );
        })}
        <ReprocessRecipeModal
          recipe={recipe}
          open={showReprocessDialog}
          onClose={() => setShowReprocessDialog(false)}
        />
        <Modal title="Recipe group" open={showGroupDialog} onCancel={() => setShowGroupDialog(false)} footer={null}>
          {!!groupInfo && (
            <View>
              <TBody>{`Group: ${groupInfo.groupId}`}</TBody>
              <TBody>{groupInfo.url}</TBody>
              <Spacer vertical={1} />
              {groupInfo.members.map(m => {
                const mediaForMember = resolveMediaForIndices(groupInfo?.socialPostInfo?.media, m.mediaIndices);
                return (
                  <View key={m.recipeId} style={{ marginBottom: globalStyleConstants.unitSize * 1.5 }}>
                    <View style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap" }}>
                      <TBody>{`${m.index}. `}</TBody>
                      <RecipeLink id={m.recipeId} newTab />
                      <Spacer horizontal={1} />
                      <TBody fontWeight="medium">{m.title}</TBody>
                      {m.isPrimary && <TBody color={colorPalette.gray}> (primary)</TBody>}
                      {!!m.excluded && <TBody color={colorPalette.gray}> (excluded)</TBody>}
                    </View>
                    {m.mediaIndices !== undefined && (
                      <>
                        <TBody color={colorPalette.gray}>{`Media indices: ${
                          m.mediaIndices.length > 0 ? m.mediaIndices.join(", ") : "<none>"
                        }`}</TBody>
                        {mediaForMember.length > 0 && (
                          <>
                            <Spacer vertical={0.5} />
                            <MediaThumbnailStrip media={mediaForMember} />
                          </>
                        )}
                      </>
                    )}
                    {/* The primary can't be excluded (it anchors the group); siblings toggle via a checkbox. */}
                    {!m.isPrimary && (
                      <>
                        <Spacer vertical={0.5} />
                        <Checkbox
                          checked={!!m.excluded}
                          disabled={excludeWaitingId !== undefined}
                          onChange={e => onToggleExcluded(m.recipeId, e.target.checked)}
                        >
                          {excludeWaitingId === m.recipeId ? "Excluded (saving…)" : "Excluded"}
                        </Checkbox>
                      </>
                    )}
                  </View>
                );
              })}
              <Spacer vertical={1} />
              <Collapse
                ghost
                items={[
                  {
                    key: "raw",
                    label: "Raw stored group JSON",
                    children: (
                      <pre
                        style={{
                          margin: 0,
                          padding: 12,
                          background: "#f5f5f5",
                          borderRadius: 4,
                          maxHeight: 400,
                          overflow: "auto",
                          fontSize: 12,
                        }}
                      >
                        {JSON.stringify(groupInfo, null, 2)}
                      </pre>
                    ),
                  },
                ]}
              />
            </View>
          )}
        </Modal>
      </View>
    );
  }
);

/** Resolve 1-based carousel positions to their media items, preserving index order and skipping gaps. */
const resolveMediaForIndices = (media: MediaItem[] | undefined, indices: number[] | undefined): MediaItem[] => {
  if (!media || !indices) {
    return [];
  }
  return indices.map(i => media.find(m => m.index === i)).filter((m): m is MediaItem => m !== undefined);
};

const MediaThumbnailStrip = React.memo((props: { media: MediaItem[] }) => {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
      {props.media.map(m => (
        <MediaThumbnail key={m.index} item={m} />
      ))}
    </View>
  );
});

const MediaThumbnail = React.memo((props: { item: MediaItem }) => {
  const { item } = props;
  const photo = item.media.type === "image" ? item.media.photo : item.media.coverPhoto;
  const isVideo = item.media.type === "video";
  // Photo's onPress="expand" uses the app's modal nav, which doesn't exist in admin (web). Open the
  // full-size image in a new tab instead.
  const openFullSize = () => window.open(getPhotoUrl(photo, "w1290"), "_blank", "noreferrer");
  const [captionCopied, setCaptionCopied] = useState(false);
  const copyCaption = () => {
    if (!item.caption) {
      return;
    }
    navigator.clipboard
      .writeText(item.caption)
      .then(() => {
        setCaptionCopied(true);
        setTimeout(() => setCaptionCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard write can fail (denied permission / insecure context); no-op for this admin convenience.
      });
  };
  return (
    <View
      style={{
        alignItems: "center",
        marginRight: globalStyleConstants.unitSize,
        marginBottom: globalStyleConstants.unitSize / 2,
      }}
    >
      <Photo source={photo} style="thumbnailSmall" sourceSize="w1290" onPress={openFullSize} />
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <TBody>{`#${item.index}${isVideo ? " (video)" : ""}`}</TBody>
        {/* Indicator that this slide has a per-slide caption; hover (native title) to read it, click to
            copy it (a title tooltip can't be selected). */}
        {!!item.caption && (
          <span
            title={`${item.caption}\n\n(click to copy)`}
            onClick={copyCaption}
            style={{ marginLeft: 4, cursor: "pointer" }}
            aria-label="Copy caption"
          >
            {captionCopied ? "✅" : "💬"}
          </span>
        )}
      </View>
    </View>
  );
});
